import { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { NavLink } from "react-router-dom";

const PortalShell = ({
  admin,
  currentPath,
  pageMeta,
  user,
  onLogout,
  onOpenChangePassword,
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = useMemo(() => {
    const sections = [
      {
        label: "Overview",
        items: [
          { to: "/home", label: "Dashboard", icon: "bi-grid-1x2" },
        ],
      },
      {
        label: "Payments",
        items: [
          {
            to: "/processtransactions",
            label: "Process transactions",
            icon: "bi-cloud-arrow-up",
          },
          {
            to: "/disbursementreport",
            label: "Transaction reports",
            icon: "bi-receipt-cutoff",
          },
          { to: "/projects", label: "Projects", icon: "bi-kanban" },
        ],
      },
    ];

    if (admin) {
      sections.push({
        label: "Administration",
        items: [
          { to: "/clients", label: "Clients", icon: "bi-buildings" },
          { to: "/users", label: "Users", icon: "bi-people" },
          {
            to: "/adminreport",
            label: "Admin reports",
            icon: "bi-clipboard-data",
          },
        ],
      });
    }

    return sections;
  }, [admin]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [currentPath]);

  const displayName =
    (user && user.user && user.user.normalizedUserName) ||
    (user && user.user && user.user.userName) ||
    "Portal User";

  const companyCode = user && user.clients && user.clients[0] && user.clients[0].companyCode;

  return (
    <div className="portal-shell">
      <button
        type="button"
        className={`portal-sidebar-backdrop${sidebarOpen ? " is-visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
        aria-label="Close navigation"
      />

      <aside className={`portal-sidebar${sidebarOpen ? " is-open" : ""}`}>
        <div className="portal-brand">
          <div className="portal-brand__logo">PM</div>
          <div>
            <p className="portal-brand__eyebrow">Corporate payments</p>
            <h1 className="portal-brand__name">PayMe Portal</h1>
          </div>
        </div>

        <div className="portal-sidebar__summary">
          <span className="portal-pill portal-pill--muted">
            {admin ? "Admin workspace" : "Operations workspace"}
          </span>
          <p>
            Securely process disbursements, monitor transaction outcomes, and keep
            teams aligned on payment operations.
          </p>
        </div>

        <nav className="portal-nav" aria-label="Primary navigation">
          {navigation.map((section) => (
            <div className="portal-nav__section" key={section.label}>
              <p className="portal-nav__label">{section.label}</p>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `portal-nav__link${isActive ? " is-active" : ""}`
                  }
                >
                  <span className={`bi ${item.icon}`} aria-hidden="true" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="portal-main">
        <header className="portal-topbar">
          <div className="portal-topbar__intro">
            <Button
              variant="link"
              className="portal-menu-toggle"
              onClick={() => setSidebarOpen((currentValue) => !currentValue)}
            >
              <span className="bi bi-list" aria-hidden="true" />
            </Button>
            <div>
              <p className="portal-page__eyebrow">{pageMeta.eyebrow}</p>
              <h2 className="portal-page__title">{pageMeta.title}</h2>
              <p className="portal-page__description">{pageMeta.description}</p>
            </div>
          </div>

          <div className="portal-topbar__actions">
            {companyCode ? (
              <span className="portal-pill portal-pill--outline">{companyCode}</span>
            ) : null}
            <span className="portal-pill portal-pill--muted">
              {admin ? "Administrator" : "Payments operator"}
            </span>
            <div className="portal-user-chip">
              <div className="portal-user-chip__avatar">{displayName.slice(0, 1)}</div>
              <div>
                <strong>{displayName}</strong>
                <p>Protected session</p>
              </div>
            </div>
            <Button variant="outline-light" onClick={onOpenChangePassword}>
              Change password
            </Button>
            <Button variant="light" onClick={onLogout}>
              Logout
            </Button>
          </div>
        </header>

        <main className="portal-content">{children}</main>
      </div>
    </div>
  );
};

export default PortalShell;
